import { useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import { Copy, FilePdf, CheckCircle } from "@phosphor-icons/react";
import MarkdownDocument from "@/components/MarkdownDocument";
import { buildYadidDocCss, renderYadidPdf } from "@/lib/yadidDoc";

/**
 * One-click memo export controls that sit under every YadidAi answer.
 *
 * "Copy as Word": renders the same MarkdownDocument to a static HTML string,
 * wraps it in a Word-friendly (`text/html`) clipboard item plus a plain-text
 * fallback so pasting into Word, Gmail, Outlook, Notion or Docs preserves the
 * bold headings and bullet lists exactly.
 *
 * "Download as PDF": lazy-loads html2pdf.js so the copilot open path stays
 * light, renders the same document into an off-DOM node, and hands it to the
 * library which produces a Letter-sized PDF via the browser's print pipeline.
 *
 * Both actions dedupe rapid re-clicks with `busy` local state.
 */
export default function MemoActions({ markdown, title = "YadidAi memo", inline = false }) {
  const [busy, setBusy] = useState(null); // "copy" | "pdf" | null
  const [copied, setCopied] = useState(false);
  const hiddenRef = useRef(null);

  // Iter 85.27 · Strip the trailing "Source meeting: <id>" provenance
  // footer from the markdown before rendering to PDF. The source
  // linkage is preserved in the note document itself
  // (note.source_meeting_note_id) for internal deep-linking; the PDF is
  // reader-facing and should carry only advisory content, not database
  // identifiers. The pattern matches both the legacy shim output and
  // the backend _render_brief_body output — both emit a horizontal
  // rule followed by a blank line followed by the italic source-id
  // paragraph.
  const stripProvenanceForExport = (md) => {
    if (!md) return md;
    return md.replace(/\n*-{3,}\s*\n+\s*\*\s*Source meeting:[^*\n]*\*\s*$/i, "").trimEnd();
  };

  const renderStaticHtml = () =>
    ReactDOMServer.renderToStaticMarkup(<MarkdownDocument>{stripProvenanceForExport(markdown)}</MarkdownDocument>);

  // Rendered PDF body: no <html>/<body> wrapper (innerHTML on a div strips them
  // AND drops the <style> block with them). Inline scoped styles keyed on the
  // unique id so html2canvas captures a fully styled DOM at positive coords.
  //
  // Layout is deliberately LEFT-ALIGNED end-to-end (masthead, metadata block,
  // body, footer) so the document reads like a partner memo — minimalist,
  // symmetric, and accessible — rather than a marketing flyer.
  //
  // Iter 85.21 · Pagination + confidentiality overhaul:
  //   · Content-flow no longer carries a per-document footer block; the
  //     running "PRIVILEGED & CONFIDENTIAL · © YEAR YADID ADVISORY · Page X/N"
  //     is drawn by jsPDF into the reserved bottom margin of every page,
  //     giving intermediate pages a real footer safe zone.
  //   · The "Prepared by: YadidAi" meta cell is removed — Yadid Advisory
  //     documents no longer self-identify as AI-produced. Classification +
  //     Date remain as a balanced two-column masthead meta strip.
  //   · Every block-level construct that must stay together across a page
  //     transition carries `break-inside: avoid` (paragraphs, list items,
  //     action-item rows) and section headings carry `break-after: avoid`
  //     so an orphaned "Action Items" heading can no longer land at the
  //     bottom of a page ahead of its content.
  const wrappedForPdf = () => {
    // html2canvas often drops ::before pseudo-elements on <li>, so we inject
    // real inline bullet spans BEFORE the class-strip pass runs. Ordered lists
    // get numbered spans; unordered lists get an ochre bullet character.
    let raw = renderStaticHtml();
    // Numbered lists: number each <li> inside every <ol>...</ol> region.
    raw = raw.replace(/<ol([^>]*)>([\s\S]*?)<\/ol>/g, (_m, attrs, inner) => {
      let i = 0;
      const withNums = inner.replace(/<li([^>]*)>/g, (_lm, lattrs) => `<li${lattrs}><span class="li-num">${++i}.</span> `);
      return `<ol${attrs} class="doc-list-num">${withNums}</ol>`;
    });
    // Bulleted lists: add an ochre bullet char before each <li>.
    raw = raw.replace(/<ul([^>]*)>([\s\S]*?)<\/ul>/g, (_m, attrs, inner) => {
      const withDots = inner.replace(/<li([^>]*)>/g, (_lm, lattrs) => `<li${lattrs}><span class="li-dot">\u2022</span> `);
      return `<ul${attrs} class="doc-list">${withDots}</ul>`;
    });
    // Strip Tailwind classes AFTER injection so our new spans + list classes survive.
    const body = raw.replace(/\sclassName="[^"]*"/g, "").replace(/\sclass="[^"]*"/g, (m) =>
      m.includes("doc-list") || m.includes("li-dot") || m.includes("li-num") ? m : ""
    );
    const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    const logoUrl = `${window.location.origin}/yadid-logo-v3.png`;
    // Iter 85.29 · CSS moved to the shared yadidDoc/docStyles builder so
    // any Yadid document export inherits the same base typography and
    // pagination rules by construction.
    return `<div id="yadidai-pdf-root">
<style>${buildYadidDocCss("yadidai-pdf-root")}</style>

<div class="masthead">
  <img src="${logoUrl}" alt="Yadid Advisory" crossorigin="anonymous" />
</div>
<div class="rule-top"></div>
<div class="meta">
  <div class="meta-cell">
    <div class="meta-label">Classification</div>
    <div class="meta-value">Confidential Memo</div>
  </div>
  <div class="meta-cell">
    <div class="meta-label">Date</div>
    <div class="meta-value">${today}</div>
  </div>
</div>
<div class="body">
${body}
</div>
</div>`;
  };

  // Word-optimised HTML: uses inline serif typography, absolute px sizing, and
  // plain <ul>/<h1>/<strong> tags so Word/Gmail/Notion pick up the structure.
  const wrappedForWord = () => {
    const body = renderStaticHtml();
    // Strip the Tailwind classes — Word won't understand them and they add noise.
    const clean = body.replace(/\sclass="[^"]*"/g, "");
    return `<html><head><meta charset="utf-8"><title>${title}</title></head>
<body style="font-family:Georgia,'Times New Roman',serif;color:#1B1E28;font-size:12pt;line-height:1.55;max-width:720px">
<style>
  h1 { font-size:20pt; font-weight:700; margin:0 0 12pt 0; }
  h2 { font-size:14pt; font-weight:700; margin:18pt 0 6pt 0; }
  h3 { font-size:12pt; font-weight:700; margin:12pt 0 4pt 0; }
  p  { margin:0 0 8pt 0; }
  ul,ol { margin:0 0 8pt 24pt; padding:0; }
  li { margin:2pt 0; }
  blockquote { border-left:3px solid #B08048; padding:2pt 12pt; margin:8pt 0; color:#4A4F5C; font-style:italic; }
  strong { font-weight:700; }
  em { font-style:italic; }
  code { font-family:'SF Mono','Courier New',monospace; font-size:10pt; background:#F5F5F4; padding:1pt 3pt; }
</style>
${clean}
</body></html>`;
  };

  const copyAsWord = async () => {
    if (busy) return;
    setBusy("copy");
    try {
      const html = wrappedForWord();
      const plain = markdown || "";
      if (navigator.clipboard?.write && window.ClipboardItem) {
        const item = new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([plain], { type: "text/plain" }),
        });
        await navigator.clipboard.write([item]);
      } else if (navigator.clipboard?.writeText) {
        // Firefox / older browsers — fall back to markdown source.
        await navigator.clipboard.writeText(plain);
      } else {
        throw new Error("Clipboard API unavailable");
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Copied — paste into Word, Gmail, or a deck");
    } catch (e) {
      toast.error("Copy failed. Your browser may block clipboard access on this page.");
    } finally { setBusy(null); }
  };

  const downloadPdf = async () => {
    if (busy) return;
    setBusy("pdf");
    try {
      const el = document.createElement("div");
      // Memo content is authored by the partner in an in-app editor. Even though
      // the shared PDF pipeline sandboxes the tree into a canvas capture (no
      // live script execution reaches the browser UI), we still sanitize before
      // innerHTML assignment so a paste of `<script>` / `<iframe>` in the memo
      // body can never introduce a live-DOM XSS vector while the offscreen
      // node exists.
      el.innerHTML = DOMPurify.sanitize(wrappedForPdf(), {
        FORBID_TAGS: ["script", "iframe", "object", "embed"],
        FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus"],
      });
      // Iter 85.22 · Tag Action Items paragraphs so they can be styled
      // as a subdued "action register" rather than a wall of heavy bold
      // titles. Each action-item paragraph shipped from toMemoMarkdown
      // has the shape `<p><strong>Task</strong><br />Owner: X · Due: Y</p>`
      // — we (a) mark the <p> with `.action-item` for CSS scoping and
      // (b) wrap the trailing Owner/Due text nodes in an `.action-meta`
      // span so we can render it as a smaller regular sub-line beneath
      // the task title. The <br /> is removed since the `.action-meta`
      // span is now `display:block`. This runs BEFORE the keep-with-next
      // wrapper so the flat sibling walk finds the correct nodes.
      const contentBody = el.querySelector("#yadidai-pdf-root .body");
      if (contentBody) {
        const heads = contentBody.querySelectorAll("h2");
        heads.forEach((h) => {
          const label = (h.textContent || "").trim().toLowerCase();
          if (label !== "action items") return;
          let n = h.nextElementSibling;
          while (n && n.tagName !== "H2" && n.tagName !== "H3") {
            if (n.tagName === "P") {
              n.classList.add("action-item");
              // Iter 85.24 · Prepend an empty square checkbox before the
              // task title. Rendered as an absolutely positioned block
              // inside the padded gutter (see .action-check CSS) so the
              // task title continues to start at the 22pt indent and
              // wrapped title lines flow underneath the title, not the
              // checkbox. Real element (not ::before) so html2canvas
              // reliably captures the outline.
              const check = document.createElement("span");
              check.className = "action-check";
              check.setAttribute("aria-hidden", "true");
              n.insertBefore(check, n.firstChild);
              const br = n.querySelector("br");
              if (br) {
                const metaSpan = document.createElement("span");
                metaSpan.className = "action-meta";
                let node = br.nextSibling;
                while (node) {
                  const next = node.nextSibling;
                  metaSpan.appendChild(node);
                  node = next;
                }
                br.remove();
                n.appendChild(metaSpan);
              }
            }
            n = n.nextElementSibling;
          }
        });
      }
      // Iter 85.21 · Keep-with-next: wrap each H2/H3 with its immediately
      // following block (paragraph, list, or nested heading) in a
      // `.keep-with-next` div so `page-break-inside: avoid` binds them
      // together. Prevents an "Action Items" / "Open Questions" heading
      // from being marooned at the bottom of a page without its body.
      if (contentBody) {
        const kids = Array.from(contentBody.children);
        for (let i = 0; i < kids.length - 1; i++) {
          const cur = kids[i];
          const nxt = kids[i + 1];
          if (!cur || !nxt) continue;
          if ((cur.tagName === "H2" || cur.tagName === "H3") && cur.tagName !== nxt.tagName) {
            const wrap = document.createElement("div");
            wrap.className = "keep-with-next";
            cur.parentNode.insertBefore(wrap, cur);
            wrap.appendChild(cur);
            wrap.appendChild(nxt);
          }
        }
      }
      // html2canvas needs the element to have real layout dimensions. Off-screen
      // via `left:-99999px` produces a blank capture in some Chrome versions.
      // Keep it at the origin with opacity:0 + pointer-events:none so it
      // participates in layout but is visually invisible and non-interactive.
      Object.assign(el.style, {
        position: "fixed",
        top: "0",
        left: "0",
        opacity: "0",
        pointerEvents: "none",
        zIndex: "-9999",
        background: "#ffffff",
      });
      document.body.appendChild(el);
      const filename = `${title.replace(/[^a-z0-9]+/gi, "_").slice(0, 60) || "yadidai_memo"}.pdf`;
      try {
        // Preload the logo so html2canvas doesn't paint before it's decoded — a
        // race that otherwise renders the letterhead as a broken-image icon.
        await new Promise((resolve) => {
          const img = el.querySelector("img");
          if (!img) return resolve();
          if (img.complete && img.naturalWidth > 0) return resolve();
          img.onload = resolve;
          img.onerror = resolve; // don't block PDF if logo 404s — still ship the memo
          setTimeout(resolve, 3000); // hard cap
        });
        // Iter 85.29 · Shared Yadid document PDF pipeline handles print
        // margins, pagebreak avoids, running header/footer, and the
        // charSpace-compensated right anchor. Memo-specific concerns
        // (masthead, meta cells, action-item tagging, keep-with-next)
        // remain the caller's responsibility and have already been
        // applied to `el` above.
        await renderYadidPdf({
          element: el.firstChild || el,
          filename,
          extraPagebreakAvoid: [".masthead", ".meta", ".keep-with-next"],
        });
        toast.success("PDF downloaded");
      } finally {
        if (el.parentNode) el.parentNode.removeChild(el);
      }
    } catch (e) {
      console.error("PDF export failed", e);
      toast.error("PDF export failed");
    } finally { setBusy(null); }
  };

  // Iter 85.97 · Two visual modes.
  //   Standalone (`inline` false) — legacy call-sites where MemoActions
  //   sits alone under a memo body; keeps its own top-rule + spacing so
  //   the export controls read as a distinct footer.
  //   Inline (`inline` true) — new NoteViewerDialog toolbar, where the
  //   parent already renders the border and controls the row. In that
  //   mode we drop the container styling and the button labels use the
  //   same shared pill classes as the sibling Refresh/Edit buttons for
  //   perfect symmetry.
  const btnClass = inline
    ? "h-8 px-3 inline-flex items-center gap-1.5 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary border border-transparent hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 disabled:opacity-50 transition-colors"
    : "text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary inline-flex items-center gap-1.5 disabled:opacity-50";
  const containerClass = inline
    ? "inline-flex items-center gap-1"
    : "flex items-center gap-2 mt-2 pt-2 border-t border-border/40";
  return (
    <div className={containerClass} data-testid="memo-actions">
      <button
        onClick={copyAsWord}
        disabled={!!busy}
        data-testid="memo-copy-word"
        className={btnClass}
      >
        {copied ? <CheckCircle size={11} weight="fill" className="text-emerald-500" /> : <Copy size={11} weight="bold" />}
        {busy === "copy" ? "Copying…" : copied ? "Copied" : "Copy as Word"}
      </button>
      {!inline && <span className="text-muted-foreground/40">·</span>}
      <button
        onClick={downloadPdf}
        disabled={!!busy}
        data-testid="memo-download-pdf"
        className={btnClass}
      >
        <FilePdf size={11} weight="bold" />
        {busy === "pdf" ? "Rendering…" : "Download as PDF"}
      </button>
      <div ref={hiddenRef} style={{ display: "none" }} aria-hidden />
    </div>
  );
}
